<script setup>
import {ref, inject, watch} from "vue";
import {useStore} from "vue2-helpers/vuex";
import userService from "@/services/user.service";
import {filterTypesByCreator, trackerTypes} from "@/constants/trackers";
import EditFolder from "@/components/trackers/EditFolder.vue";
import SelectTrackers from "@/components/manage/SelectTrackersModal.vue";
import trackersService from "@/services/trackers.service";
import trackersFoldersService from "@/services/trackers-folders.service";
import {useRouter} from "vue2-helpers/vue-router";

const store = useStore();
const router = useRouter();

const filterByCreator = inject('filterByCreator');
const setNeedUpdateSearch = inject('setNeedUpdateSearch');
const folder = inject('folder');
const folderId = folder.value.id;
const search = inject('search');
const updateInfo = inject('updateInfo');
const trackersCounts = inject('trackersCounts');

const linkCopied = ref(false);
const editFolderModal = ref(null);
const selectTrackersModal = ref(null);
const trackersToAdd = ref([]);

watch(() => store.getters.settings, (settings) => {
    if (settings.trackers_filter !== undefined) {
        if (settings.trackers_filter) {
            filterByCreator.value = settings.trackers_filter;
        } else if (store.getters.isAdmin) {
            filterByCreator.value = 'related-to-me';
        } else {
            filterByCreator.value = 'all';
        }
    }
}, {immediate: true});

async function setFilterByCreator(value) {
    const {data} = await userService.saveTrackerFilter(value);
    store.commit('setUser', data);
}

function copyPublicLink() {
    let link = document.location.origin + '/public-collections/' + folder.value.referral_url
    navigator.clipboard.writeText(link)
    this.linkCopied = true;

    setTimeout(() => {
        this.linkCopied = false;
    }, 2000);
}

async function remove() {
    if ((trackersCounts.value.active > 0 || trackersCounts.value.archived > 0) && !confirm("Delete folder and all its trackers?")) {
        return;
    }

    await trackersFoldersService.delete(folderId);
    router.push('/trackers');
}

async function archive() {
    if (trackersCounts.value.active > 0 && !confirm("Archive folder and all it's trackers?")) {
        return;
    }

    await trackersFoldersService.archive(folderId);
}

async function unarchive() {
    if (trackersCounts.value.archive > 0 && !confirm("Unarchive folder and all it's trackers?")) {
        return;
    }

    await trackersFoldersService.unarchive(folderId);
}

async function selectTrackersToAdd() {
    const allTrackers = trackersService.getList(folder.value.type === trackerTypes.PUBLIC ? trackerTypes.PUBLIC : '');
    const addedTrackersIds = trackersFoldersService.getTrackersIds(folderId);

    Promise.all([allTrackers, addedTrackersIds]).then(([{data: allTrackers}, {data: addedTrackersIds}]) => {
        trackersToAdd.value = allTrackers.filter(item => !addedTrackersIds.includes(item.id));
    });

    selectTrackersModal.value.open();
}

async function addTrackers(trackers) {
    await trackersFoldersService.addTrackers(folderId, trackers.map(tracker => tracker.id));
    setNeedUpdateSearch();
}
</script>

<template>
    <div class="topbar topbar--full">
        <router-link to="/trackers" class="link-back">All Trackers</router-link>

        <h2 class="tracker-name heading2">
            {{ folder.name }}
        </h2>

        <div class="topbar__wrap">
            <input
                v-model="search"
                type="text"
                v-debounce:500="setNeedUpdateSearch"
                placeholder="Search for a tracker"
                class="form-search-primary"
            />

            <div class="topbar__inner mb-0 pt-2">
                <div class="topbar__key">
                    <div class="topbar__key__footer">
                        <span class="topbar__key__label">Filter by:</span>

                        <template v-for="(label, value) in filterTypesByCreator">
                            <div
                                v-if="value !== 'related-to-me' || store.state.user.is_admin"
                                :key="value" class="key__filter"
                                @click="setFilterByCreator(value)"
                            >
                                <span class="key__filter__wrapper">
                                    <span
                                        class="key__filter__tag key__filter__tag--classic"
                                        :class="{key__filter__connection: filterByCreator != value}">
                                        <span>{{label}}</span>
                                    </span>
                                </span>
                            </div>
                        </template>
                    </div>
                </div>

                <div class="topbar__side">
                    <template v-if="folder.type === trackerTypes.PUBLIC">
                        <span
                            v-if="linkCopied"
                            class="copy-link mb-0 mr-3"
                        >
                            Link copied
                        </span>

                        <a v-else
                           class="copy-link mb-0 mr-3"
                           @click="copyPublicLink"
                        >
                            Copy Public Link
                        </a>
                    </template>

                    <ul class="actions-list" v-if="trackersCounts !== null">
                        <li>
                            <div>
                                <b-dropdown
                                    v-if="folder.user_permissions.includes('tracker-folder-manage') || folder.user_permissions.includes('tracker-folder-add-trackers')"
                                    text="Actions"
                                    class="topbar__side__actions icons-list dropdown-simple"
                                >
                                    <template v-if="folder.user_permissions.includes('tracker-folder-manage')">
                                        <b-dropdown-item class="action icons-list__edit" @click="editFolderModal.open()">
                                            Edit Folder
                                        </b-dropdown-item>

                                        <b-dropdown-item class="action icons-list__delete" @click="remove">
                                            Delete
                                        </b-dropdown-item>

                                        <b-dropdown-item class="action icons-list__archive" v-if="!folder.archived" @click="archive">
                                            Archive
                                        </b-dropdown-item>

                                        <b-dropdown-item class="action icons-list__archive" v-else @click="unarchive">
                                            Unarchive
                                        </b-dropdown-item>
                                    </template>

                                    <b-dropdown-item
                                        v-if="folder.user_permissions.includes('tracker-folder-add-trackers')"
                                        class="action icons-list__add"
                                        @click="selectTrackersToAdd"
                                    >
                                        Add Trackers
                                    </b-dropdown-item>

                                </b-dropdown>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <EditFolder
            ref="editFolderModal"
            @folderSaved="updateInfo"
        />

        <SelectTrackers
            ref="selectTrackersModal"
            :trackers-list="trackersToAdd"
            @trackersSelected="addTrackers"
        />
    </div>
</template>

<style scoped>

</style>
